/**
 * SEO helpers shared by every marketing page: canonical path per locale, full
 * hreflang alternate set, and the BCP-47 tag for `<html lang>` / og:locale.
 */

import {
  localizedHref,
  type SupportedMarketingLocale,
} from "@/i18n/marketing";

interface AlternateLink {
  hreflang: string;
  path: string;
}

/** BCP-47 tag emitted to `<html lang>` / og:locale for each marketing locale. */
const HTML_LANG: Record<SupportedMarketingLocale, string> = {
  en: "en",
  es: "es-419",
  "pt-BR": "pt-BR",
  fr: "fr",
  ja: "ja",
};

/** hreflang value used in alternate links — Google reads regional variants here. */
const HREFLANG: Record<SupportedMarketingLocale, string> = {
  en: "en",
  es: "es-419",
  "pt-BR": "pt-BR",
  fr: "fr",
  ja: "ja",
};

/** All marketing locales, in stable order. */
export const MARKETING_LOCALES: SupportedMarketingLocale[] = [
  "en",
  "es",
  "pt-BR",
  "fr",
  "ja",
];

export function htmlLangFor(locale: SupportedMarketingLocale): string {
  return HTML_LANG[locale];
}

export function canonicalFor(
  englishPath: string,
  locale: SupportedMarketingLocale,
): string {
  return localizedHref(englishPath, locale);
}

/** Build the full alternate-link set for a marketing path (English version). */
export function alternatesFor(englishPath: string): AlternateLink[] {
  const links: AlternateLink[] = MARKETING_LOCALES.map((l) => ({
    hreflang: HREFLANG[l],
    path: localizedHref(englishPath, l),
  }));
  // x-default points at the unprefixed English URL (Google convention).
  links.push({ hreflang: "x-default", path: localizedHref(englishPath, "en") });
  return links;
}
